/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LearnerShell, { ProgressBar, LearnerStatusBadge, EmptyState } from "./learnerShell";
import LmsAssetRenderer from "../template/admin/lmsAssetRenderer";
import LmsQuizResultSummary from "./LmsQuizResultSummary";
import { globalAlert } from "../actions/commonActions";
import LearnerContent from "../admin/learnerContent";
import {
  learnerCourseDetailApi,
  learnerEnrolApi,
  learnerAssetProgressApi,
  learnerCertificatesApi,
} from "./_learnerActions";
import { learnerPath } from "./_learnerConfig";

/* ── defensive helpers (titles are optional everywhere) ── */

const idOf = (x) => x?._id || x?.id || x?.assetId || null;
const CERTIFICATE_NODE_KEY = "__course_certificate__";
const sortByOrder = (arr = []) =>
  [...arr].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
const listOfCertificates = (data) =>
  Array.isArray(data) ? data : data?.list || data?.certificates || [];
const matchesCourseCertificate = (cert, course = {}, routeCourseId = "") => {
  const normalize = (value) => String(value || "").trim().toLowerCase();
  const certCourse = cert?.course || {};
  const certKeys = [
    certCourse?._id,
    certCourse?.id,
    certCourse?.slug,
    cert?.courseId,
    cert?.courseSlug,
  ]
    .map(normalize)
    .filter(Boolean);
  const courseKeys = [
    course?._id,
    course?.id,
    course?.courseId,
    course?.slug,
    routeCourseId,
  ]
    .map(normalize)
    .filter(Boolean);
  return certKeys.some((key) => courseKeys.includes(key));
};
const pdfViewerUrl = (url = "") => {
  if (!url) return "";
  const [base, hash = ""] = String(url).split("#");
  const params = new URLSearchParams(hash);
  params.set("toolbar", "0");
  params.set("navpanes", "0");
  params.set("scrollbar", "0");
  return `${base}#${params.toString()}`;
};

// Unwrap { serviceAudience|serviceArea, asset } wrappers into plain assets.
const unwrapAssets = (list = []) =>
  sortByOrder(list).map((w) =>
    w?.asset && typeof w.asset === "object"
      ? {
        ...w.asset,
        _wrapId: w?._id,
        serviceArea: w?.serviceArea || w.asset?.serviceArea,
        supportLabel: w?.supportLabel || w?.serviceArea || w.asset?.supportLabel,
      }
      : w
  );

const assetDone = (a) =>
  !!(a?.completed || a?.isCompleted || a?.progress?.completed || a?.assetProgress?.completed);

const quizIdOf = (qz) => qz?._id || qz?.quizId || (typeof qz === "string" ? qz : null);
const quizPassed = (qz) =>
  !!(qz?.passed || qz?.isPassed || (qz?.status || "").toLowerCase() === "passed");
const quizResultOf = (qz) => qz?.latestResult || qz?.result || null;
const normalizeQuizResult = (result = {}) => ({
  ...result,
  percent: result.percent ?? result.scorePercent ?? 0,
  feedback: result.feedback || [],
});
const serviceAreaLabel = (value = "") => {
  const v = String(value || "").toUpperCase();
  if (v === "ESSEX") return "Essex";
  if (v === "SUFFOLK") return "Suffolk";
  return value || "";
};
const serviceAreaForAudience = (audience = "") => {
  const v = String(audience || "").toUpperCase();
  if (v === "ESHS") return "ESSEX";
  if (v === "SSHS") return "SUFFOLK";
  return "";
};
const targetServiceArea = (payload = {}) =>
  String(
    payload?.learnerContext?.serviceArea ||
      payload?.previewConfig?.serviceArea ||
      serviceAreaForAudience(
        payload?.learnerContext?.serviceAudience || payload?.previewConfig?.serviceAudience
      ) ||
      ""
  ).toUpperCase();
const filterSupportForArea = (assets = [], area = "") => {
  const target = String(area || "").toUpperCase();
  if (!target) return assets;
  return assets.filter((asset) => String(asset?.serviceArea || asset?.supportLabel || "").toUpperCase() === target);
};

// Build the linear learning tree from the learner course-detail payload.
// Every node: { key, kind, label, moduleId?, sectionId?, assets?, quiz?, locked }
const buildNodes = (payload, t) => {
  const course = payload?.course || payload || {};
  const modules = sortByOrder(payload?.modules || course?.modules || []);
  const finalQuiz = payload?.courseQuiz || payload?.finalQuiz || course?.quiz || null;
  const supportAreaFilter = targetServiceArea(payload);
  const nodes = [];

  // Course intro — the API returns the persona's single introAsset (or a list).
  const intro = payload?.introAsset
    ? [payload.introAsset]
    : unwrapAssets(course?.introAssets || payload?.introAssets || []);
  if (intro.length) {
    nodes.push({
      key: "course-intro",
      kind: "course_intro",
      label: t("learner.lms.player.courseIntro", "Course introduction"),
      short: t("learner.lms.player.courseIntro", "Course introduction"),
      assets: intro,
      locked: false,
    });
  }

  modules.forEach((m, mi) => {
    const moduleId = idOf(m);
    const mNum = mi + 1;
    const mLabel = m?.title || t("learner.lms.player.moduleN", "Module {{n}}", { n: mNum });
    const locked = !!m?.locked;
    const meta = { moduleTitle: mLabel, moduleId, moduleNum: mNum };

    const mIntro = unwrapAssets(m?.introductionAssets || []);
    if (mIntro.length) {
      nodes.push({
        key: `m-${moduleId}-intro`,
        kind: "module_intro",
        label: `${mLabel} · ${t("learner.lms.player.intro", "Introduction")}`,
        short: t("learner.lms.player.intro", "Introduction"),
        ...meta,
        assets: mIntro,
        locked,
      });
    }

    sortByOrder(m?.sections || []).forEach((s, si) => {
      const sectionId = idOf(s);
      // Sections are "Parts" in the course language — always "Part N" (legacy
      // stored titles are ignored so learners see the same clean sequence the
      // author does).
      const sLabel = t("learner.lms.player.partN", "Part {{n}}", { n: si + 1 });
      nodes.push({
        key: `m-${moduleId}-s-${sectionId}`,
        kind: "section",
        label: `${mLabel} · ${sLabel}`,
        short: sLabel,
        shortLabel: sLabel,
        ...meta,
        sectionId,
        assets: sortByOrder(s?.assets || []),
        quiz: s?.quiz || null,
        locked,
      });
    });

    const done = unwrapAssets(m?.completionAssets || []);
    if (done.length) {
      nodes.push({
        key: `m-${moduleId}-completion`,
        kind: "completion",
        label: `${mLabel} · ${t("learner.lms.player.completion", "Key-Tips")}`,
        short: t("learner.lms.player.completion", "Key-Tips"),
        ...meta,
        assets: done,
        locked,
      });
    }

    const support = filterSupportForArea(
      unwrapAssets(m?.supportServicesAssets || m?.supportServiceAssets || []),
      supportAreaFilter
    );
    if (support.length) {
      const supportArea = serviceAreaLabel(support[0]?.serviceArea || support[0]?.supportLabel);
      const supportTitle = supportArea
        ? `${supportArea} ${t("learner.lms.player.support", "Support services")}`
        : t("learner.lms.player.support", "Support services");
      nodes.push({
        key: `m-${moduleId}-support`,
        kind: "support_service",
        label: `${mLabel} - ${supportTitle}`,
        short: supportTitle,
        ...meta,
        assets: support,
        locked,
      });
    }

    if (m?.quiz && quizIdOf(m.quiz)) {
      nodes.push({
        key: `m-${moduleId}-quiz`,
        kind: "module_quiz",
        label: `${mLabel} · ${m?.quiz?.title || t("learner.lms.player.quiz", "Quiz")}`,
        short: m?.quiz?.title || t("learner.lms.player.quiz", "Quiz"),
        ...meta,
        quiz: m.quiz,
        locked,
      });
    }
  });

  if (finalQuiz && quizIdOf(finalQuiz)) {
    nodes.push({
      key: "final-quiz",
      kind: "course_quiz",
      label: finalQuiz?.title || t("learner.lms.player.finalAssessment", "Final assessment"),
      short: finalQuiz?.title || t("learner.lms.player.finalAssessment", "Final assessment"),
      quiz: finalQuiz,
      locked: false,
    });
  }

  return nodes;
};

// Group the flat node list into { topLevel[], modules: [{ moduleId, title, num,
// nodes[], locked }], final } for the grouped sidebar. Order is preserved.
const groupNodes = (nodes) => {
  const topLevel = [];
  const final = [];
  const map = new Map();
  const order = [];
  nodes.forEach((n) => {
    if (n.kind === "course_intro") return topLevel.push(n);
    if (n.kind === "course_quiz") return final.push(n);
    if (!map.has(n.moduleId)) {
      map.set(n.moduleId, {
        moduleId: n.moduleId,
        title: n.moduleTitle,
        num: n.moduleNum,
        locked: n.locked,
        nodes: [],
      });
      order.push(n.moduleId);
    }
    const group = map.get(n.moduleId);
    group.nodes.push(n);
    group.locked = group.nodes.every((node) => node.locked);
  });
  return { topLevel, modules: order.map((id) => map.get(id)), final };
};

// Icon for a leaf node by kind (used in the grouped sidebar).
const nodeIcon = (kind) => {
  if (kind.includes("quiz")) return "fa-question-circle";
  if (kind === "section_video") return "fa-video";
  if (kind === "section_summary") return "fa-file-lines";
  if (kind === "support_service") return "fa-hands-helping";
  if (kind === "completion") return "fa-flag-checkered";
  if (kind === "module_intro" || kind === "course_intro") return "fa-play-circle";
  return "fa-file-lines";
};

// A node counts as complete when all its assets are done and its quiz (if any)
// is passed.
const nodeComplete = (node, doneSet, passedSet) => {
  const assets = node.assets || [];
  const hasCompletionTarget = assets.length > 0 || !!node.quiz;
  const assetsOk = assets.every((a) => doneSet.has(String(idOf(a))));
  const quizOk = !node.quiz || passedSet.has(String(quizIdOf(node.quiz)));
  return hasCompletionTarget && assetsOk && quizOk;
};

const progressFromNodes = (nodes = [], doneSet = new Set(), passedSet = new Set()) => {
  let total = 0;
  let done = 0;
  nodes.forEach((node) => {
    // Part summary PDFs are optional reference material. They stay visible in
    // the player, but never affect learner completion or its percentage.
    if (node?.kind === "section_summary") return;
    (node.assets || []).forEach((asset) => {
      const assetId = idOf(asset);
      if (!assetId) return;
      total += 1;
      if (doneSet.has(String(assetId))) done += 1;
    });
    const quizId = quizIdOf(node?.quiz);
    if (quizId) {
      total += 1;
      if (passedSet.has(String(quizId))) done += 1;
    }
  });
  return total > 0 ? Math.round((done / total) * 100) : null;
};

const alwaysUnlockedNode = (node) =>
  node?.kind === "section_summary" ||
  node?.kind === "completion" ||
  node?.kind === "support_service";

const requiredNodeComplete = (node, doneSet, passedSet) =>
  node?.kind === "section_summary" || nodeComplete(node, doneSet, passedSet);

const expandSectionNode = (node, t) => {
  if (node?.kind !== "section") return [node];
  const videoAssets = (node.assets || []).filter((asset) => asset?.type === "video");
  // Authors can attach an optional summary-slide PDF to a Part (admin.lms.section.uploadSummary).
  // These used to be silently dropped here — never rendered, so a learner could
  // never view/complete them even when a module's completionCriteria requires it.
  const summaryAssets = (node.assets || []).filter((asset) => asset?.type === "pdf");
  const expanded = [];

  videoAssets.forEach((asset, index) => {
    const videoLabel =
      videoAssets.length > 1
        ? `${t("learner.lms.player.video", "Video")} ${index + 1}`
        : t("learner.lms.player.video", "Video");
    const title = asset?.title ? `${videoLabel}: ${asset.title}` : videoLabel;
    expanded.push({
      ...node,
      key: `${node.key}-video-${idOf(asset) || index}`,
      kind: "section_video",
      label: `${node.label} - ${title}`,
      short: title,
      assets: [asset],
      quiz: null,
    });
  });

  summaryAssets.forEach((asset, index) => {
    const summaryLabel =
      summaryAssets.length > 1
        ? `${t("learner.lms.player.summarySlide", "Summary Slide")} ${index + 1}`
        : t("learner.lms.player.summarySlide", "Summary Slide");
    const title = asset?.title ? `${summaryLabel}: ${asset.title}` : summaryLabel;
    expanded.push({
      ...node,
      key: `${node.key}-summary-${idOf(asset) || index}`,
      kind: "section_summary",
      label: `${node.label} - ${title}`,
      short: summaryLabel,
      assets: [asset],
      quiz: null,
    });
  });

  if (node.quiz) {
    expanded.push({
      ...node,
      key: `${node.key}-quiz`,
      kind: "section_quiz",
      label: `${node.label} - ${t("learner.lms.player.quiz", "Quiz")}`,
      short: `${node.shortLabel || node.short} - ${t("learner.lms.player.quiz", "Quiz")}`,
      assets: [],
    });
  }

  return expanded.length ? expanded : [node];
};

// ─────────────────────────────────────────────────────────────────────────────
// Course detail + learning player. Not enrolled → overview with Enrol.
// Enrolled → sidebar tree (locked states) + asset stage with real progress
// reporting, section/module/final quizzes via the shared LmsQuizRunner.
// ─────────────────────────────────────────────────────────────────────────────
const LearnerCourse = () => {
  const { t } = useTranslation();
  const { orgName, courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPlayer = location.pathname.includes(`/course/${courseId}/player`);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  const [currentKey, setCurrentKey] = useState(null);
  const [doneSet, setDoneSet] = useState(new Set());
  const [passedSet, setPassedSet] = useState(new Set());
  const [assetProgressMap, setAssetProgressMap] = useState(new Map());
  const [progressPercent, setProgressPercent] = useState(null);
  const [celebrate, setCelebrate] = useState(null);
  const [certificateFallback, setCertificateFallback] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [openQuizResult, setOpenQuizResult] = useState(null);
  const startedRef = useRef(false);
  const currentKeyRef = useRef(null);
  const [courseSlug, setCourseSlug] = useState("");
  const quizResultModalRef = useRef(null);
  const quizResultTriggerRef = useRef(null);

  // Quiz-result modal: move focus in on open, trap it (Tab doesn't escape to the
  // page behind the overlay), close on Escape, and restore focus to whatever
  // opened it — none of which the dialog did before (WCAG 2.1.2/2.4.3).
  useEffect(() => {
    if (!openQuizResult) return;
    quizResultTriggerRef.current = document.activeElement;
    const modal = quizResultModalRef.current;
    const closeBtn = modal?.querySelector(".lms-player-result-modal-close");
    closeBtn?.focus();

    const handleKeyDown = (ev) => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        setOpenQuizResult(null);
        return;
      }
      if (ev.key === "Tab" && modal) {
        const focusables = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (ev.shiftKey && document.activeElement === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && document.activeElement === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      quizResultTriggerRef.current?.focus?.();
    };
  }, [openQuizResult]);

  useEffect(() => {
    load(true);
  }, [courseId]);

  const load = async (first = false) => {
    if (first) setLoading(true);
    setError("");
    try {
      const res = await learnerCourseDetailApi(courseId);
      if (res?.data?.status === 200) {
        const d = res?.data?.data || {};
        setCourseSlug(d?.course?.slug);
        setPayload(d);
        // Seed completion state from the REAL progress arrays (verified live):
        // enrolment.courseIntroProgress[] and module.progress.{moduleIntroProgress,
        // sectionProgress[{section, assetProgress, quiz, quizPassed}],
        // completionAssetProgress, supportServiceAssetProgress} — entries are
        // { asset: <id>, status: not_started|in_progress|completed, completedAt }.
        const done = new Set();
        const passed = new Set();
        const enr = d?.enrolment || d?.enrollment;
        const progressByAsset = new Map();
        const markProg = (list = []) =>
          (list || []).forEach((p) => {
            const aid = p?.asset?._id || p?.asset;
            if (aid) progressByAsset.set(String(aid), p);
            if (aid && (p?.status === "completed" || p?.completedAt || p?.completed))
              done.add(String(aid));
          });
        markProg(enr?.courseIntroProgress);
        const course = d?.course || d || {};
        (d?.modules || course?.modules || []).forEach((m) => {
          const pr = m?.progress || {};
          markProg(pr.moduleIntroProgress);
          (pr.sectionProgress || []).forEach((sp) => {
            markProg(sp?.assetProgress);
            const qid = sp?.quiz?._id || sp?.quiz;
            if (sp?.quizPassed && qid) passed.add(String(qid));
          });
          markProg(pr.completionAssetProgress);
          markProg(pr.supportServiceAssetProgress);
          const mqid = quizIdOf(m?.quiz);
          if (mqid && (pr?.quizPassed || pr?.moduleQuizPassed || quizPassed(m?.quiz)))
            passed.add(String(mqid));
          // Legacy fallback: some payloads may flag assets directly.
          (m?.sections || []).forEach((s) => {
            (s?.assets || []).forEach((a) => assetDone(a) && done.add(String(idOf(a))));
            if (s?.quiz && quizPassed(s.quiz)) passed.add(String(quizIdOf(s.quiz)));
          });
        });
        const fq = d?.courseQuiz || d?.finalQuiz || course?.quiz;
        const fqid = quizIdOf(fq);
        if (fqid && (quizPassed(fq) || enr?.examResult)) passed.add(String(fqid));
        setDoneSet(done);
        setPassedSet(passed);
        setAssetProgressMap(progressByAsset);
        if (enr?.progressPercent != null) setProgressPercent(enr.progressPercent);
      } else {
        setError(res?.data?.message || t("common.somethingWentWrong"));
      }
    } catch (e) {
      setError(e?.response?.data?.message || t("common.somethingWentWrong"));
    }
    setLoading(false);
  };

  const course = payload?.course || payload || {};
  const courseRecordId = course?._id || course?.courseId || "";
  const routeCourseId = encodeURIComponent(courseSlug || course?.slug || courseId || "");
  const enrolment = payload?.enrolment || payload?.enrollment || null;
  const enrolled = !!enrolment;
  const enrolmentId = enrolment?._id || enrolment?.enrolmentId;
  const completed = (enrolment?.status || "").toLowerCase() === "completed";

  useEffect(() => {
    let active = true;
    const loadCertificateFallback = async () => {
      if (!completed || !isPlayer || !enrolled) {
        setCertificateFallback(null);
        return;
      }
      setCertificateLoading(true);
      try {
        const res = await learnerCertificatesApi();
        if (!active) return;
        if (res?.data?.status === 200) {
          const certificates = listOfCertificates(res?.data?.data).filter(
            (cert) =>
              String(cert?.certificateType || cert?.type || "course").toLowerCase() !== "module"
          );
          setCertificateFallback(
            certificates.find((cert) => matchesCourseCertificate(cert, course, courseId)) ||
              certificates[0] ||
              null
          );
        }
      } catch {
        if (active) setCertificateFallback(null);
      } finally {
        if (active) setCertificateLoading(false);
      }
    };
    loadCertificateFallback();
    return () => {
      active = false;
    };
  }, [completed, isPlayer, enrolled, courseRecordId, courseSlug, courseId]);

  useEffect(() => {
    if (!courseSlug || !orgName || courseId === courseSlug) return;
    navigate(learnerPath(orgName, `course/${encodeURIComponent(courseSlug)}${isPlayer ? "/player" : ""}`), {
      replace: true,
    });
  }, [courseSlug, courseId, orgName, isPlayer, navigate]);

  const rawNodes = useMemo(
    () => payload
      ? buildNodes(payload, t).flatMap((node) => expandSectionNode(node, t))
      : [],
    [payload, t]
  );
  const nodes = useMemo(() => {
    let blocked = false;
    return rawNodes.map((n) => {
      const canAlwaysOpen = alwaysUnlockedNode(n);
      const locked = canAlwaysOpen ? false : n.locked || blocked;
      const next = { ...n, locked };
      if (!canAlwaysOpen && (locked || !nodeComplete(n, doneSet, passedSet))) blocked = true;
      return next;
    });
  }, [rawNodes, doneSet, passedSet]);
  const grouped = useMemo(() => groupNodes(nodes), [nodes]);
  const computedProgressPercent = useMemo(
    () => progressFromNodes(rawNodes, doneSet, passedSet),
    [rawNodes, doneSet, passedSet]
  );
  const visibleProgressPercent =
    completed
      ? 100
      : computedProgressPercent != null
        ? computedProgressPercent
        : progressPercent;
  const embeddedCourseCertificate = payload?.certificate || payload?.courseCertificate || null;
  const courseCertificate = embeddedCourseCertificate || certificateFallback || null;
  const certificateNumber =
    courseCertificate?.certificateNumber || certificateFallback?.certificateNumber || celebrate?.certificateNumber || "";
  const certificatePreviewUrl =
    courseCertificate?.certificatePreviewUrl || courseCertificate?.certificateUrl || "";
  const certificatePdfDownloadUrl =
    courseCertificate?.certificateDownloadUrl || courseCertificate?.certificateUrl || "";
  const certificateImagePreviewUrl =
    courseCertificate?.certificateImageUrl || courseCertificate?.imageUrl || "";
  const certificateImageDownloadUrl =
    courseCertificate?.certificateImageDownloadUrl ||
    courseCertificate?.certificateImageUrl ||
    courseCertificate?.imageUrl ||
    "";
  const certificateViewUrl = certificateImagePreviewUrl || certificatePreviewUrl;
  const certificateDownloadUrl = certificatePdfDownloadUrl || certificateImageDownloadUrl;
  const certificateDownloadName = `${certificateNumber || "certificate"}${
    certificatePdfDownloadUrl ? ".pdf" : ".svg"
  }`;
  const compactPdfPreviewUrl = pdfViewerUrl(certificatePreviewUrl);
  const certificateEnabled = course?.hasCertificate === true;
  const showCertificateNav = certificateEnabled || completed || !!courseCertificate;
  const certificateUnlocked = completed && showCertificateNav;
  const viewingCertificate = currentKey === CERTIFICATE_NODE_KEY;

  // Resume: land on the first incomplete, unlocked node.
  useEffect(() => {
    if (!isPlayer || !enrolled || !nodes.length || startedRef.current) return;
    startedRef.current = true;
    const next =
      nodes.find((n) => !n.locked && !requiredNodeComplete(n, doneSet, passedSet)) ||
      nodes[0];
    setCurrentKey(next.key);
  }, [nodes, enrolled, isPlayer]);

  const idx = viewingCertificate ? nodes.length : nodes.findIndex((n) => n.key === currentKey);
  const node = viewingCertificate ? null : idx >= 0 ? nodes[idx] : null;
  const currentNodeComplete = node ? nodeComplete(node, doneSet, passedSet) : false;
  const nextNodeAfterCurrent =
    !viewingCertificate && idx >= 0
      ? nodes
          .slice(idx + 1)
          .find((n, offset) => !rawNodes[idx + 1 + offset]?.locked || alwaysUnlockedNode(n))
      : null;
  const hasNextNode = !!nextNodeAfterCurrent || (!!node && certificateUnlocked && idx === nodes.length - 1);

  useEffect(() => {
    currentKeyRef.current = currentKey;
  }, [currentKey]);

  // Videos complete only by being watched to the end (auto — see reportAsset on
  // the renderer's onEnded). Manual "Mark as done" is offered ONLY for the
  // node's non-video assets; a node whose sole pending item is a video has no
  // manual control.
  const markableAssets = (node?.assets || []).filter((a) => a?.type !== "video");
  const markableAllDone = markableAssets.every((a) => doneSet.has(String(idOf(a))));
  const pendingVideo = (node?.assets || []).some(
    (a) => a?.type === "video" && !doneSet.has(String(idOf(a)))
  );
  const hasPdfAsset = (node?.assets || []).some((a) => a?.type === "pdf");

  // Sidebar accordion: expand the module that owns the current node.
  const [openModules, setOpenModules] = useState({});
  useEffect(() => {
    if (node?.moduleId) setOpenModules((o) => ({ ...o, [node.moduleId]: true }));
  }, [node?.moduleId]);
  const toggleModule = (id) =>
    setOpenModules((o) => ({ ...o, [id]: !o[id] }));
  /* ── enrol / retake ── */
  const enrol = async (retake = false) => {
    setEnrolling(true);
    try {
      const res = await learnerEnrolApi(courseRecordId || courseId, retake);
      const { status, message } = res?.data || {};
      if (status === 200) {
        startedRef.current = false;
        await load();
      } else globalAlert("warning", message);
    } catch (e) {
      globalAlert("error", e?.response?.data?.message);
    }
    setEnrolling(false);
  };

  /* ── progress reporting ── */
  const locationPayload = (n) => ({
    enrolmentId,
    courseId: courseRecordId || courseId,
    assetLocation:
      n.kind === "module_quiz" || n.kind === "course_quiz" || n.kind === "section_quiz"
        ? undefined
        : n.kind === "section_video" || n.kind === "section_summary"
          ? "section"
          : n.kind,
    ...(n.moduleId ? { moduleId: n.moduleId } : {}),
    ...(n.sectionId ? { sectionId: n.sectionId } : {}),
  });

  const applyProgressResponse = (data) => {
    if (data?.progressPercent != null) setProgressPercent(data.progressPercent);
    if (data?.examResult) {
      setCelebrate(data.examResult);
      setTimeout(() => load(false), 500);
    }
  };

  const reportAsset = async (n, asset, { completed: isDone, watchedSeconds, durationSeconds }) => {
    if (!enrolmentId || n.locked) return;
    const aId = idOf(asset);
    try {
      const res = await learnerAssetProgressApi({
        ...locationPayload(n),
        assetId: aId,
        type: asset?.type,
        ...(watchedSeconds != null ? { watchedSeconds } : {}),
        ...(durationSeconds != null ? { durationSeconds } : {}),
        completed: !!isDone,
      });
      const { status, data } = res?.data || {};
      if (status === 200) {
        const ap = data?.assetProgress;
        const completedNow = isDone || ap?.completed || ap?.status === "completed";
        if (completedNow) {
          setDoneSet((prev) => new Set(prev).add(String(aId)));
        }
        if (ap && aId) {
          setAssetProgressMap((prev) => {
            const next = new Map(prev);
            next.set(String(aId), ap);
            return next;
          });
        }
        applyProgressResponse(data);
      }
    } catch (e) {
      /* progress reporting is best-effort; hard errors surface via client */
    }
  };

  // Non-interactive assets (rich_text) complete on first open of their node.
  useEffect(() => {
    if (!isPlayer || !node || node.locked || !enrolmentId) return;
    (node.assets || [])
      .filter((a) => a?.type === "rich_text" && !doneSet.has(String(idOf(a))))
      .forEach((a) => reportAsset(node, a, { completed: true }));
  }, [currentKey, enrolmentId, isPlayer]);

  const markNodeViewed = () => {
    // Explicit "mark complete" for anything not auto-completed (e.g. a video the
    // learner scrubbed, or an asset type without a view event).
    const pendingAssets = (node?.assets || []).filter(
      (a) => !doneSet.has(String(idOf(a))) && a?.type !== "video"
    );
    if (pendingAssets.length) {
      setDoneSet((prev) => {
        const next = new Set(prev);
        pendingAssets.forEach((asset) => next.add(String(idOf(asset))));
        return next;
      });
    }
    pendingAssets.forEach((a) => reportAsset(node, a, { completed: true }));
  };

  const go = (key) => {
    if (key === CERTIFICATE_NODE_KEY) {
      if (certificateUnlocked) setCurrentKey(CERTIFICATE_NODE_KEY);
      return;
    }
    const target = nodes.find((n) => n.key === key);
    if (!target || target.locked) return;
    setCurrentKey(key);
  };
  const goBy = (delta) => {
    let ni = idx + delta;
    while (ni >= 0 && ni < nodes.length && nodes[ni].locked) ni += delta;
    if (ni >= 0 && ni < nodes.length) go(nodes[ni].key);
  };
  const goToNext = () => {
    if (!node) return;
    const nextKey = nextNodeAfterCurrent?.key || (certificateUnlocked ? CERTIFICATE_NODE_KEY : null);
    if (!nextKey) return;
    if (hasPdfAsset || (markableAssets.length > 0 && !markableAllDone)) {
      markNodeViewed();
      setCurrentKey(nextKey);
      return;
    }
    if (currentNodeComplete) setCurrentKey(nextKey);
  };

  // Launch the dedicated full-screen assessment page for a quiz node, passing
  // the enrolment + display context via router state.
  const openQuiz = (n) => {
    const subtitle = n.moduleTitle
      ? `${n.moduleTitle}${n.shortLabel ? " · " + n.shortLabel : ""}`
      : "";
    navigate(
      learnerPath(orgName, `course/${routeCourseId}/quiz/${quizIdOf(n.quiz)}?enrolmentId=${encodeURIComponent(enrolmentId || "")}`),
      {
      state: {
        enrolmentId,
        title: n.quiz?.title || "",
        subtitle,
        maxAttempts: n.quiz?.maxAttempts ?? null,
        timeLimitMinutes: n.quiz?.timeLimitMinutes ?? null,
        passMark: n.quiz?.passMark ?? null,
        questionCount: n.quiz?.questionCount ?? null,
      },
    });
  };

  /* ── render ── */

  if (loading) {
    return (
      <LearnerShell title={t("learner.lms.course.title", "Course")}>
        <div className="lms-portal-loading">
          <i className="fas fa-circle-notch fa-spin mr-2"></i>
          {t("common.loading", "Loading…")}
        </div>
      </LearnerShell>
    );
  }

  if (error) {
    return (
      <LearnerShell title={t("learner.lms.course.title", "Course")}>
        <div className="lms-portal-error" role="alert">{error}</div>
      </LearnerShell>
    );
  }

  const courseTitle = course?.title || t("learner.lms.course.untitled", "Course");
  const modules = sortByOrder(payload?.modules || course?.modules || []);
  /* Not enrolled → overview + enrol */
  if (!enrolled && isPlayer) {
    return (
      <LearnerShell title={courseTitle}>
        <div className="lms-course-overview">
          {course?.thumbnailUrl && (
            <img className="lms-course-overview-thumb" src={course.thumbnailUrl} alt="" />
          )}
          <h1 className="learner-course-title">{courseTitle}</h1>
          {course?.description && <p className="lms-portal-sub my-4">{course.description}</p>}
          <p className="text-muted">
            {t("learner.lms.course.moduleCount", "{{n}} modules", {
              n: (payload?.modules || course?.modules || []).length,
            })}
          </p>
          <button
            className="btn lms-btn-primary"
            disabled={enrolling}
            onClick={() => enrol(false)}
          >
            {enrolling ? (
              <i className="fas fa-circle-notch fa-spin"></i>
            ) : (
              t("learner.lms.course.enrol", "Enrol")
            )}
          </button>
        </div>
      </LearnerShell>
    );
  }

  /* Enrolled → player */
  if (!isPlayer) {
    return (
      <LearnerShell title={courseTitle}>
        <div className="lms-course-detail-page">
          <section
            className={`lms-course-detail-hero ${
              course?.thumbnailUrl ? "has-thumbnail" : "without-thumbnail"
            }`}
          >
            {course?.thumbnailUrl && (
              <img className="lms-course-detail-thumb" src={course.thumbnailUrl} alt="" />
            )}
            <div className="lms-course-detail-main">
              <div className="lms-course-detail-kicker">
                <i className="fas fa-book-open"></i>
                {t("learner.lms.course.courseOverview", "Course overview")}
              </div>
              <h1 className="lms-portal-title">{courseTitle}</h1>
              {course?.description && <p className="lms-portal-sub">{course.description}</p>}


              <div className="lms-course-detail-actions">
                {!enrolled ? (
                  <button
                    className="btn lms-btn-primary"
                    disabled={enrolling}
                    onClick={() => enrol(false)}
                  >
                    {enrolling ? (
                      <i className="fas fa-circle-notch fa-spin"></i>
                    ) : (
                      t("learner.lms.course.enrol", "Enroll Now")
                    )}
                  </button>
                ) : (
                  <button
                    className="btn lms-btn-primary"
                    onClick={() => navigate(learnerPath(orgName, `course/${routeCourseId}/player`))}
                  >
                    {completed
                      ? t("learner.lms.course.reviewCourse", "Review course")
                      : t("learner.lms.course.continue", "Continue")}
                    <i className="fas fa-arrow-right ms-2"></i>
                  </button>
                )}


              </div>
            </div>
          </section>



          {enrolled && visibleProgressPercent != null && (
            <section className="lms-course-detail-progress">
              <div>
                <strong>{t("learner.lms.course.progress", "Progress")}</strong>
                <span>{Math.round(visibleProgressPercent)}%</span>
              </div>
              <ProgressBar percent={visibleProgressPercent} />
            </section>
          )}

          <section className="lms-course-detail-modules">
            {/* <div className="lms-course-detail-section-head">
              <h2>{t("learner.lms.player.courseContent", "Course Modules")}</h2>
            </div> */}

            {modules.length === 0 ? (
              <EmptyState icon="fa-folder-open" text={t("learner.lms.player.empty", "Nothing to show yet.")} />
            ) : (
              <LearnerContent
              coursedSlug={courseSlug}
              courseId={courseSlug || courseId}
              />
            )}
          </section>
        </div>
      </LearnerShell>
    );
  }
  return (
    <LearnerShell title={courseTitle} wide>
      <div className="lms-player-head">
        <div>
          <h1 className="lms-portal-title mb-1">{courseTitle}</h1>
        </div>
        {visibleProgressPercent != null && (
          <div className="lms-player-progress">
            <ProgressBar percent={visibleProgressPercent} />
            <span>{Math.round(visibleProgressPercent)}%</span>
          </div>
        )}
      </div>

      {celebrate && (
        <div className="lms-portal-congrats" role="status">
          <i className="fas fa-trophy mr-2"></i>
          {t("learner.lms.course.completedBanner", "Congratulations — you've completed this course!")}
        </div>
      )}

      <div className="lms-player-shell">
        {/* Course Content sidebar — grouped by module */}
        <nav className="lms-player-sidebar" aria-label={t("learner.lms.player.contents", "Course contents")}>
          <div className="lms-cc-head">
            <h2 className="lms-cc-title">{t("learner.lms.player.courseContent", "Course Content")}</h2>
            <div className="d-flex align-items-center mb-3 mt-2 flex-wrap" style={{ gap: '10px' }}>
              <LearnerStatusBadge 
                status={
                  completed || visibleProgressPercent === 100
                    ? "completed"
                    : visibleProgressPercent > 0
                    ? "inProgress"
                    : enrolment?.status
                } 
                t={t} 
              />
              {completed && (
                <button
                  className="btn btn-sm rounded-pill d-inline-flex align-items-center"
                  style={{ backgroundColor: '#d35400', color: 'white', fontWeight: 600, padding: '4px 12px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)', border: 'none', fontSize: '0.8rem' }}
                  onClick={() => enrol(true)}
                  disabled={enrolling}
                >
                  <i className="fas fa-sync-alt" style={{ marginRight: '6px' }}></i>
                  {t("learner.lms.course.retake", "Retake course")}
                </button>
              )}
            </div>
            {visibleProgressPercent != null && (
              <>
                <ProgressBar percent={visibleProgressPercent} small />
                <span className="lms-cc-pct">
                  {t("learner.lms.player.pctComplete", "{{n}}% Completed", {
                    n: Math.round(visibleProgressPercent),
                  })}
                </span>
              </>
            )}
          </div>

          {/* leaf row renderer */}
          {(() => {
            const Leaf = (n) => {
              const isDone = nodeComplete(n, doneSet, passedSet);
              return (
                <button
                  key={n.key}
                  className={`lms-cc-item ${n.key === currentKey ? "active" : ""} ${n.locked ? "locked" : ""} ${isDone ? "done" : ""}`}
                  onClick={() => go(n.key)}
                  disabled={n.locked}
                  aria-current={n.key === currentKey ? "true" : undefined}
                >
                  <i
                    className={`fas ${n.locked ? "fa-lock" : isDone ? "fa-check-circle" : nodeIcon(n.kind)
                      } lms-cc-icon`}
                    aria-hidden="true"
                  ></i>
                  <span className="lms-cc-label">{n.short || n.label}</span>
                </button>
              );
            };

            return (
              <>
                {grouped.topLevel.map(Leaf)}

                {grouped.modules.map((mod) => {
                  const open = !!openModules[mod.moduleId];
                  const modDone =
                    mod.nodes.length > 0 &&
                    mod.nodes.every((n) => requiredNodeComplete(n, doneSet, passedSet));
                  return (
                    <div className="lms-cc-group" key={mod.moduleId}>
                      <button
                        className={`lms-cc-module ${mod.locked ? "locked" : ""} ${modDone ? "done" : ""}`}
                        onClick={() => !mod.locked && toggleModule(mod.moduleId)}
                        disabled={mod.locked}
                        aria-expanded={open}
                      >
                        <i
                          className={`fas ${mod.locked ? "fa-lock" : modDone ? "fa-check-circle" : "fa-book-open"
                            } lms-cc-icon`}
                          aria-hidden="true"
                        ></i>
                        <span className="lms-cc-label">
                          {t("learner.lms.player.moduleNum", "Module {{n}}", { n: mod.num })}
                          {mod.title ? `: ${mod.title}` : ""}
                        </span>
                        {!mod.locked && (
                          <i className={`fas fa-chevron-${open ? "up" : "down"} lms-cc-caret`} aria-hidden="true"></i>
                        )}
                      </button>
                      {open && !mod.locked && (
                        <div className="lms-cc-children">{mod.nodes.map(Leaf)}</div>
                      )}
                    </div>
                  );
                })}

                {grouped.final.map(Leaf)}

                {showCertificateNav && (
                  <button
                    key={CERTIFICATE_NODE_KEY}
                    className={`lms-cc-item lms-cc-certificate ${viewingCertificate ? "active" : ""} ${!certificateUnlocked ? "locked" : ""} ${certificateUnlocked ? "done" : ""}`}
                    onClick={() => go(CERTIFICATE_NODE_KEY)}
                    disabled={!certificateUnlocked}
                    aria-current={viewingCertificate ? "true" : undefined}
                  >
                    <i
                      className={`fas ${!certificateUnlocked ? "fa-lock" : "fa-certificate"} lms-cc-icon`}
                      aria-hidden="true"
                    ></i>
                    <span className="lms-cc-label">
                      {t("learner.lms.cert.courseCertificate", "Course Certificate")}
                    </span>
                  </button>
                )}
              </>
            );
          })()}
        </nav>

        {/* Stage */}
        <div className="lms-player-stage">
          {viewingCertificate && (
            <div className="lms-player-card lms-player-cert-card">
              {certificateLoading && (
                <div className="lms-portal-loading">
                  <i className="fas fa-circle-notch fa-spin mr-2"></i>
                  {t("common.loading", "Loading certificate...")}
                </div>
              )}

              {!certificateLoading && (
                <>
                  <div className="lms-cert-simple-head">
                    <h2 className="lms-cert-simple-title">
                      <i className="fas fa-certificate" aria-hidden="true"></i>
                      {t("learner.lms.cert.courseCertificate", "Course Certificate")}
                    </h2>
                    <div className="lms-cert-simple-actions">
                      {certificateDownloadUrl && (
                        <a
                          href={certificateDownloadUrl}
                          download={certificateDownloadName}
                          className="btn lms-btn-primary"
                        >
                          <i className="fas fa-download" aria-hidden="true"></i>
                          {t("common.download", "Download")}
                        </a>
                      )}
                      {certificateViewUrl && (
                        <a
                          href={certificateViewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn lms-btn-ghost"
                        >
                          <i className="fas fa-expand" aria-hidden="true"></i>
                          {t("learner.lms.cert.viewFullSize", "View full size")}
                        </a>
                      )}
                    </div>
                  </div>

                  {certificateImagePreviewUrl ? (
                    <div className="lms-player-cert-image-frame">
                      <img
                        src={certificateImagePreviewUrl}
                        alt={t("learner.lms.cert.courseCertificate", "Course Certificate")}
                      />
                    </div>
                  ) : certificatePreviewUrl ? (
                    <iframe
                      className="lms-player-cert-frame"
                      src={compactPdfPreviewUrl}
                      title={t("learner.lms.cert.courseCertificate", "Course Certificate")}
                    />
                  ) : (
                    <EmptyState
                      icon="fa-certificate"
                      text={t(
                        "learner.lms.cert.generating",
                        "Your certificate is being prepared. Please open My Certificates again in a moment."
                      )}
                    />
                  )}
                </>
              )}
            </div>
          )}
          {!viewingCertificate && !node && (
            <EmptyState icon="fa-folder-open" text={t("learner.lms.player.empty", "Nothing to show yet.")} />
          )}
          {!viewingCertificate && node && (
            <div className="lms-player-card">
              <div className="lms-player-node-head">
                <span className="lms-player-node-eyebrow">
                  <i className={`fas ${nodeIcon(node.kind)}`} aria-hidden="true"></i>
                  {node.moduleTitle
                    ? t("learner.lms.player.moduleNum", "Module {{n}}", {
                      n: node.moduleNum,
                    }) + (node.moduleTitle ? `: ${node.moduleTitle}` : "")
                    : courseTitle}
                </span>
                <h2 className="lms-player-node-title">{node.short || node.label}</h2>
              </div>

              {node.locked && (
                <div className="lms-quiz-blocked" role="alert">
                  <i className="fas fa-lock mr-2"></i>
                  {t("learner.lms.player.locked", "Complete the previous modules to unlock this one.")}
                </div>
              )}

              {!node.locked && (
                <>
                  {(node.assets || []).map((a, i) => (
                    <LmsAssetRenderer
                      asset={a}
                      key={idOf(a) || i}
                      resumeSeconds={
                        Number(assetProgressMap.get(String(idOf(a)))?.watchedSeconds || 0)
                      }
                      onAssetEvent={(ev) => reportAsset(node, a, { completed: true, ...ev })}
                      onProgress={(p) =>
                        reportAsset(node, a, {
                          completed: false,
                          watchedSeconds: p.watchedSeconds,
                          durationSeconds: p.durationSeconds,
                        })
                      }
                    />
                  ))}

                  {pendingVideo && (
                    <p className="lms-player-autohint">
                      <i className="fas fa-circle-play mr-1" aria-hidden="true"></i>
                      {t(
                        "learner.lms.player.videoAutoComplete",
                        "Finish watching the video to unlock Next."
                      )}
                    </p>
                  )}

                  {/* Section / module / final quiz — opens the dedicated
                      full-screen assessment page. */}
                  {(()=>{

                    const questionCount=node?.quiz?.questionCount;
                    const passMark=node?.quiz?.passMark;
                    const quizResult = quizResultOf(node?.quiz);
                    const normalizedQuizResult = quizResult ? normalizeQuizResult(quizResult) : null;
                    const hasPassedQuiz = passedSet.has(String(quizIdOf(node.quiz)));

                  return(node.quiz && quizIdOf(node.quiz) && (
                    <div
                      className={`lms-player-quizcard text-center d-flex flex-column align-items-center ${hasPassedQuiz ? "is-passed" : ""}`}
                      style={{ backgroundColor: '#fff', border: '1px solid #eaeaea', borderRadius: '12px', padding: '30px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', flexDirection: 'column' }}
                    >
                      <div className="mb-3">
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', backgroundColor: hasPassedQuiz ? '#e6f4ea' : '#eef2ff', color: hasPassedQuiz ? '#28a745' : '#322F91', fontSize: '2rem' }}>
                          <i className={`fas ${hasPassedQuiz ? "fa-check" : "fa-question"}`}></i>
                        </div>
                      </div>

                      <div style={{ fontSize: '0.75rem', fontWeight: 'bold', letterSpacing: '1px', color: hasPassedQuiz ? '#28a745' : '#322F91', textTransform: 'uppercase', marginBottom: '8px' }}>
                        {hasPassedQuiz ? "COMPLETED · QUIZ" : "QUIZ"}
                      </div>

                      <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                        {node.quiz?.title || t("learner.lms.player.quiz", "Quiz")}
                      </h3>

                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '20px' }}>
                        {hasPassedQuiz ? "Great work — you have completed this quiz." : "Check your understanding before continuing."}
                      </div>

                      <div className="d-flex justify-content-center flex-wrap mb-4" style={{ gap: '10px' }}>
                        {questionCount > 0 && (
                          <span style={{ backgroundColor: '#1f2937', color: '#fff', borderRadius: '8px', padding: '8px 14px', fontSize: '0.9rem', fontWeight: '500', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <i className="fas fa-list-ol" style={{ color: '#9ca3af', marginRight: '8px' }}></i>Total Questions: {questionCount}
                          </span>
                        )}
                        {hasPassedQuiz ? (
                          <>
                            {normalizedQuizResult && (
                              <span style={{ backgroundColor: '#0f5132', color: '#fff', borderRadius: '8px', padding: '8px 14px', fontSize: '0.9rem', fontWeight: '500', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <i className="fas fa-tachometer-alt" style={{ color: '#75b798', marginRight: '8px' }}></i>Your score: {Math.round(Number(normalizedQuizResult.percent) || 0)}%
                              </span>
                            )}
                            {normalizedQuizResult?.marksScored != null && (
                              <span style={{ backgroundColor: '#432874', color: '#fff', borderRadius: '8px', padding: '8px 14px', fontSize: '0.9rem', fontWeight: '500', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <i className="fas fa-award" style={{ color: '#b695f0', marginRight: '8px' }}></i>Marks: {normalizedQuizResult.marksScored}/{normalizedQuizResult.totalMarks}
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            {passMark > 0 && (
                              <span style={{ backgroundColor: '#322F91', color: '#fff', borderRadius: '8px', padding: '8px 14px', fontSize: '0.9rem', fontWeight: '500', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <i className="fas fa-bullseye" style={{ color: '#a5a3ff', marginRight: '8px' }}></i>Pass mark: {passMark}%
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      {hasPassedQuiz ? (
                        <div className="d-flex flex-column mx-auto w-100" style={{ gap: '15px' }}>
                          <div className="text-center" style={{ backgroundColor: '#e6f4ea', color: '#1e8e3e', padding: '12px', borderRadius: '30px', fontWeight: 'bold', fontSize: '0.95rem', width: '100%' }}>
                            <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>Passed
                          </div>
                          {normalizedQuizResult && (
                            <button
                              className="btn lms-btn-primary w-100"
                              onClick={() => setOpenQuizResult({ quiz: node.quiz, result: normalizedQuizResult })}
                              type="button"
                              style={{ fontWeight: '600', padding: '12px', borderRadius: '30px' }}
                            >
                              <i className="fas fa-chart-bar" style={{ marginRight: '8px' }}></i>View result
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="mx-auto w-100">
                          <button
                            className="btn lms-btn-primary w-100"
                            onClick={() => openQuiz(node)}
                            style={{ fontWeight: '600', padding: '12px', borderRadius: '30px' }}
                          >
                            <i className="fas fa-play" style={{ marginRight: '8px' }}></i>Start quiz
                          </button>
                        </div>
                      )}
                    </div>
                  ))
             }) ()}

                  {/* Navigation */}
                  <div className="lms-player-nav">
                    <button
                      className="btn lms-btn-primary"
                      disabled={idx <= 0}
                      onClick={() => goBy(-1)}
                      style={idx <= 0 ? { cursor: 'not-allowed' } : {}}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      {t("learner.lms.player.previous", "Previous")}
                    </button>
                    {markableAssets.length > 0 && !pendingVideo && !hasPdfAsset && (
                      <button
                        className="btn lms-btn-ghost"
                        onClick={markNodeViewed}
                        disabled={markableAllDone}
                      >
                        <i className="fas fa-check me-2"></i>
                        {markableAllDone
                          ? t("learner.lms.player.completed", "Completed")
                          : t("learner.lms.player.markComplete", "Mark as done")}
                      </button>
                    )}
                    <button
                      className="btn lms-btn-primary"
                      disabled={
                        !hasNextNode ||
                        pendingVideo ||
                        (!hasPdfAsset && markableAssets.length === 0 && !currentNodeComplete)
                      }
                      onClick={goToNext}
                    >
                      {t("learner.lms.player.next", "Next")}
                      <i className="fas fa-arrow-right mx-2"></i>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {openQuizResult && (
        <div className="lms-player-result-modal-backdrop" role="presentation">
          <div
            className="lms-player-result-modal-scrim"
            onClick={() => setOpenQuizResult(null)}
          ></div>
          <div
            className="lms-player-result-modal"
            role="dialog"
            aria-modal="true"
            ref={quizResultModalRef}
          >
            <button
              className="lms-player-result-modal-close"
              onClick={() => setOpenQuizResult(null)}
              type="button"
              aria-label={t("common.close", "Close")}
            >
              <i className="fas fa-times"></i>
            </button>
            <LmsQuizResultSummary
              result={openQuizResult.result}
              passMark={openQuizResult.result?.passMark ?? openQuizResult.quiz?.passMark}
              scorePercent={openQuizResult.result?.percent}
              retake={() => {}}
              loading={false}
              attemptsRemaining={0}
              attemptNumber={openQuizResult.result?.attemptNumber}
              maxAttempts={openQuizResult.result?.maxAttempts ?? openQuizResult.quiz?.maxAttempts}
            />
          </div>
        </div>
      )}
    </LearnerShell>
  );
};

export default LearnerCourse;
